/**
 * Venue Data Store
 * Stores ALL community venue data with prioritized loading:
 * 1. Current venue loads FIRST (fast initial render)
 * 2. Remaining venues load in background (for MQTT updates)
 */

import { defineStore } from "pinia";
import { ref, computed, markRaw, shallowReactive } from "vue";
import { useEdgeApi } from "@/composables/useEdgeApi";
import { useIncidentResolveReasons } from "@/composables/useIncidentResolveReasons";
import type {
    Venue,
    Level,
    EdgeDevice,
    EdgeUser,
    Marker,
    Safezone,
    Incident,
    Space,
    Building,
    IncidentSafeZone,
    IncidentComment,
    EndUserAssisting,
    IncidentDuplicate,
} from "@/types/api";
import type {
    VenueData,
    LevelData,
    VenueSummary,
    VenueDelta,
} from "@/types/venueState";
import { createEmptyVenueData, createEmptyLevelData } from "@/types/venueState";
import { useStaticDataStore } from "./staticData";
import { isEndUserHydrated } from "@/helpers/endUserVisibility";

export const useVenueDataStore = defineStore("venueData", () => {
    const edgeApi = useEdgeApi();
    const { fetchDrillResolveReason } = useIncidentResolveReasons();
    const staticDataStore = useStaticDataStore();

    // ================================================================
    // STATE
    // ================================================================

    const venues = ref<Map<number, VenueData>>(new Map());
    const allVenuesList = ref<Venue[]>([]); // Quick reference to all venues
    const allLevelsList = ref<Level[]>([]); // Quick reference to all levels

    /** Global off-site users (no venue/level) - preserves data for when they return on-site */
    const offsiteUsers = ref<EdgeUser[]>([]);

    // Location update un-deep-proxied tick counter to trigger UI
    const locationUpdateTick = ref(0);

    // Level data version counter — increments whenever fetchLevelData completes.
    // Used by viewContext computed (spaces, buildings) to force reactivity despite
    // the levelData Map being markRaw (which prevents Vue from tracking Map mutations).
    const levelDataVersion = ref(0);

    // ================================================================
    // INTERNAL — O(1) Index Maps (hot-path performance for MQTT deltas)
    // ================================================================

    /** userId → venueId : O(1) lookup to find which venue a user belongs to */
    const _userVenueIndex = new Map<number, number>();

    /** userId → EdgeUser : O(1) direct object lookup to bypass array scans entirely */
    const _userObjectCache = new Map<number, EdgeUser>();

    /** Tracks which userIds are in the offsiteUsers array : O(1) offsite lookup */
    const _offsiteUserIds = new Set<number>();

    /** levelId → venueId : O(1) level-to-venue resolution */
    const _levelVenueIndex = new Map<number, number>();

    /**
     * incidentId → venueId : O(1) incident-to-venue resolution. Used by
     * applyDelta to route incident-scoped relations (incidentSafeZones,
     * endUsersAssisting, incidentDuplicates) and per-comment routing
     * without needing a delta envelope. Populated by fetchActiveIncidents
     * and the incidents block of applyDelta; cleared on $reset.
     */
    const _incidentVenueIndex = new Map<number, number>();

    /**
     * item ID → { venueId, levelId } : O(1) cross-venue/level locator for
     * venue items. Tracking both fields lets cross-level moves resolve the
     * previous entry's venue unambiguously (a marker can technically move
     * across venues, and the previous level may even have been evicted
     * from `_levelVenueIndex`).
     */
    type ItemLocation = { venueId: number; levelId: number };
    const _markerLevelIndex = new Map<number, ItemLocation>();
    const _deviceLevelIndex = new Map<string, ItemLocation>();

    /** Venues that have completed their initial user bootstrap (fetchVenueUsers) */
    const _usersBootstrapped = new Set<number>();

    /**
     * User IDs that were mutated since the last map render cycle.
     * Accumulated across multiple applyDelta calls (multiple MQTT frames between
     * throttled renders). Consumed by takeDirtyUserIds() inside addEndUsers.
     */
    const _dirtyUserIds = new Set<number>();

    /** Called with each user ID an MQTT user delta touches. */
    const _userUpdateListeners = new Set<(userId: number) => void>();

    /** Subscribe to per-user delta updates; returns an unsubscribe function. */
    function onUserUpdated(listener: (userId: number) => void): () => void {
        _userUpdateListeners.add(listener);
        return () => _userUpdateListeners.delete(listener);
    }

    /** Return a snapshot of dirty user IDs and clear the set. */
    function takeDirtyUserIds(): Set<number> {
        const snap = new Set(_dirtyUserIds);
        _dirtyUserIds.clear();
        return snap;
    }

    /**
     * User IDs that were removed from a level (went offsite, changed venue, or
     * were deleted) since the last map render cycle. Consumed by takeRemovedUserIds()
     * inside addEndUsersDirty to avoid the O(N) incomingIds Set scan in updateMapMarkers.
     */
    const _removedUserIds = new Set<number>();

    /** Return a snapshot of removed user IDs and clear the set. */
    function takeRemovedUserIds(): Set<number> {
        const snap = new Set(_removedUserIds);
        _removedUserIds.clear();
        return snap;
    }

    // ================================================================
    // INTERNAL — Paginated fetch helper
    // ================================================================
    const MAX_PAGE_SIZE = 200;

    /**
     * Share one in-flight request per key. A forced call queues behind the
     * current one, so its fresher result is written last.
     */
    function dedupeInFlight<K, T>(
        map: Map<K, Promise<T>>,
        key: K,
        force: boolean,
        run: () => Promise<T>,
    ): Promise<T> {
        const inflight = map.get(key);
        if (inflight && !force) return inflight;

        const promise: Promise<T> = (
            inflight ? inflight.catch(() => undefined).then(() => run()) : run()
        ).finally(() => {
            if (map.get(key) === promise) map.delete(key);
        });
        map.set(key, promise);
        return promise;
    }

    /**
     * Fetch all pages of a paginated endpoint and return the combined data array.
     * Handles the backend's max pageSize=200 constraint by looping through pages.
     */
    async function fetchAllPages<T>(
        endpoint: string,
        params: Record<string, any>,
    ): Promise<T[]> {
        const firstRes = await edgeApi.get<any>(endpoint, {
            params: { ...params, page: 1, pageSize: MAX_PAGE_SIZE },
        });
        const responseData = firstRes.data;

        // Handle both response formats:
        // - Paginated: { data: [...], total: N, page: N, pageSize: N }
        // - Flat array: [...]
        if (Array.isArray(responseData)) {
            return responseData as T[];
        }

        const firstPage: T[] = responseData?.data ?? [];
        const total =
            typeof responseData?.total === "number" ? responseData.total : 0;
        const totalPages = Math.ceil(total / MAX_PAGE_SIZE);

        if (totalPages <= 1) return firstPage;

        // Fetch remaining pages in parallel — page 1 already told us the
        // total, so pages 2..N are independent and can be requested
        // concurrently instead of sequentially.
        const pagePromises: Promise<T[]>[] = [];
        for (let page = 2; page <= totalPages; page++) {
            pagePromises.push(
                edgeApi
                    .get<any>(endpoint, {
                        params: { ...params, page, pageSize: MAX_PAGE_SIZE },
                    })
                    .then((res) => (res.data?.data ?? []) as T[]),
            );
        }
        const remainingPages = await Promise.all(pagePromises);

        // Flatten in page order
        const all = firstPage;
        for (const page of remainingPages) {
            all.push(...page);
        }
        return all;
    }

    /** Rebuild _userVenueIndex for a single venue (after fetch or bulk update) */
    function _rebuildUserVenueIndexForVenue(
        venueId: number,
        users: EdgeUser[],
    ): void {
        // Collect keys to remove first (safe — no delete during iteration)
        const toRemove: number[] = [];
        _userVenueIndex.forEach((vid, uid) => {
            if (vid === venueId) toRemove.push(uid);
        });
        for (const uid of toRemove) {
            _userVenueIndex.delete(uid);
            _userObjectCache.delete(uid);
        }
        // Add current users
        for (const user of users) {
            _userVenueIndex.set(user.id, venueId);
            _userObjectCache.set(user.id, user);
        }
    }

    /** Rebuild _levelVenueIndex from allLevelsList */
    function _rebuildLevelVenueIndex(): void {
        _levelVenueIndex.clear();
        for (const level of allLevelsList.value) {
            _levelVenueIndex.set(level.id, level.venueId);
        }
    }

    // ================================================================
    // INTERNAL — User Location Helpers
    // ================================================================

    /** Remove a user from their current location (venue or offsite) and clear location indices */
    function _removeUserFromLocation(userId: number): void {
        const vid = _userVenueIndex.get(userId);
        if (vid !== undefined) {
            const vd = venues.value.get(vid);
            if (vd) {
                const idx = vd.endUsers.findIndex((x) => x.id === userId);
                if (idx > -1) vd.endUsers.splice(idx, 1);
            }
            _userVenueIndex.delete(userId);
            // Track removal so the map can skip the O(N) stale-scan on dirty renders
            _removedUserIds.add(userId);
        }
        if (_offsiteUserIds.has(userId)) {
            const idx = offsiteUsers.value.findIndex((x) => x.id === userId);
            if (idx > -1) offsiteUsers.value.splice(idx, 1);
            _offsiteUserIds.delete(userId);
            _removedUserIds.add(userId);
        }
    }

    /** Place a user at a venue (number) or offsite (null) and update indices */
    function _placeUser(user: EdgeUser, targetVenueId: number | null): void {
        // Dedup by id at the array boundary. The cache (`_userObjectCache`) is
        // the fast dedup path for applyDelta, but it can be cleared while the
        // backing arrays still hold the user — e.g. trimUserCaches() runs on
        // every venue switch and a re-visited venue short-circuits
        // fetchVenueUsers via _usersBootstrapped, so the array is never reset.
        // Without this fallback, a cache-miss MQTT delta would push a second
        // entry for an id that's already present.
        if (targetVenueId === null) {
            const arr = offsiteUsers.value;
            const idx = arr.findIndex((u) => u.id === user.id);
            if (idx > -1) {
                Object.assign(arr[idx] as object, user);
                user = arr[idx];
            } else {
                arr.push(user);
            }
            _offsiteUserIds.add(user.id);
        } else {
            const arr = ensureVenue(targetVenueId).endUsers;
            const idx = arr.findIndex((u) => u.id === user.id);
            if (idx > -1) {
                Object.assign(arr[idx] as object, user);
                user = arr[idx];
            } else {
                arr.push(user);
            }
            _userVenueIndex.set(user.id, targetVenueId);
        }
        _userObjectCache.set(user.id, user);
    }

    /** Move a user from their current location to a new one */
    function _moveUser(user: EdgeUser, toVenueId: number | null): void {
        _removeUserFromLocation(user.id);
        _placeUser(user, toVenueId);
    }

    // Initialization state
    const initialized = ref(false);
    const initializing = ref(false);
    const initError = ref<string | null>(null);
    const backgroundLoadComplete = ref(false);

    // ================================================================
    // HELPERS
    // ================================================================

    function ensureVenue(venueId: number): VenueData {
        if (!venues.value.has(venueId)) {
            venues.value.set(venueId, createEmptyVenueData(venueId));
        }
        return venues.value.get(venueId)!;
    }

    function ensureLevel(venueId: number, levelId: number): LevelData {
        const venue = ensureVenue(venueId);
        if (!venue.levelData.has(levelId)) {
            // shallowReactive tracks top-level property assignments (e.g.
            // levelData.devices = [...]) without deep-proxying every element
            // inside the arrays. This avoids creating 750+ Proxy wrappers per
            // level when fetchLevelData populates devices/markers/safezones.
            //
            // Reactivity to the map component is driven by levelDataVersion
            // (a manual counter), not by Vue's deep proxy tracking — so
            // shallowReactive is sufficient and eliminates significant GC
            // pressure on the MQTT hot path.
            venue.levelData.set(
                levelId,
                shallowReactive(createEmptyLevelData(levelId)),
            );
        }
        return venue.levelData.get(levelId)!;
    }

    // ================================================================
    // GETTERS - Aggregated (Cross-Venue)
    // ================================================================

    const allVenues = computed<Venue[]>(() => allVenuesList.value);

    const allActiveIncidents = computed<Incident[]>(() => {
        const all: Incident[] = [];
        venues.value.forEach((v) => all.push(...v.activeIncidents));
        return all;
    });

    const totalIncidentCount = computed(() => allActiveIncidents.value.length);

    const venueSummaries = computed<VenueSummary[]>(() => {
        const summaries: VenueSummary[] = [];
        venues.value.forEach((v) => {
            if (!v.venue) return;
            summaries.push({
                venueId: v.venueId,
                venueName: v.venue.name,
                activeIncidentCount: v.activeIncidents.length,
                confirmedIncidentCount: v.activeIncidents.filter(
                    (i) => i.status === "confirmed",
                ).length,
                unconfirmedIncidentCount: v.activeIncidents.filter(
                    (i) => i.status === "unconfirmed",
                ).length,
                hasShooterIncident: v.activeIncidents.some(
                    (i) => i.type === "shooter",
                ),
                lastActivity: v.lastFetched,
            });
        });
        return summaries;
    });

    const criticalVenues = computed(() =>
        venueSummaries.value.filter(
            (s) => s.hasShooterIncident || s.confirmedIncidentCount > 0,
        ),
    );

    // ================================================================
    // GETTERS - Per Venue
    // ================================================================

    const getVenueData = (venueId: number) => venues.value.get(venueId);
    const getVenue = (venueId: number) =>
        venues.value.get(venueId)?.venue ?? null;
    const getLevels = (venueId: number) =>
        venues.value.get(venueId)?.levels ?? [];
    const getActiveIncidents = (venueId: number) =>
        venues.value.get(venueId)?.activeIncidents ?? [];

    /** Get a single incident by ID across all venues */
    const getIncidentById = (incidentId: number) =>
        allActiveIncidents.value.find((i) => i.id === incidentId) ?? null;

    const getLevelData = (venueId: number, levelId: number) =>
        venues.value.get(venueId)?.levelData.get(levelId);

    const getIncidentComments = (venueId: number, incidentId: number) =>
        venues.value.get(venueId)?.incidentComments[incidentId] ?? [];

    const getDevices = (venueId: number, levelId: number) =>
        (getLevelData(venueId, levelId)?.devices ?? []).filter(
            (d) => d.type !== "smart_beacon"
        );
    const getMarkers = (venueId: number, levelId: number) =>
        getLevelData(venueId, levelId)?.markers ?? [];
    /**
     * Master safezone list for a venue. Safezones are venue-scoped; level
     * filtering happens at the consumer (viewContext) via Safezone.levelId.
     * This avoids the join-table fetch that previously hid safezones not
     * currently assigned to any incident.
     */
    const getSafezones = (venueId: number) =>
        venues.value.get(venueId)?.safezones ?? [];

    /** Get all users for a venue (stored at venue level) */
    const getAllEndUsersInVenue = (venueId: number): EdgeUser[] =>
        venues.value.get(venueId)?.endUsers ?? [];

    /** Hydrated user in this venue by id, O(1). Reactive through locationUpdateTick. */
    function getVenueEndUser(venueId: number, userId: number): EdgeUser | undefined {
        void locationUpdateTick.value;
        if (_userVenueIndex.get(userId) !== venueId) return undefined;
        const user = _userObjectCache.get(userId);
        return user && isEndUserHydrated(user) ? user : undefined;
    }

    /** Hydrated off-site (still tracked) user by id, O(1). Reactive through locationUpdateTick. */
    function getOffsiteEndUser(userId: number): EdgeUser | undefined {
        void locationUpdateTick.value;
        if (!_offsiteUserIds.has(userId)) return undefined;
        const user = _userObjectCache.get(userId);
        return user && isEndUserHydrated(user) ? user : undefined;
    }

    /** Get end users for a specific venue/level (or off-site users) */
    function getEndUsers(venueId: number, levelId: number): EdgeUser[] {
        const _vd = venues.value.get(venueId);
        if (!_vd) return [];

        // Only show users that belong to this venue AND match the level.
        // Users without a venueId (off-site / transient MQTT deltas) must not
        // leak into the list — they will be fetched and properly placed once
        // their full profile is loaded via fetchUserById.
        return _vd.endUsers.filter(
            (u) => u.venueId === venueId && u.levelId === levelId,
        );
    }

    /** Get all off-site users (global, not per-venue) */
    const getOffsiteUsers = (): EdgeUser[] => offsiteUsers.value;

    const getLevel = (venueId: number, levelId: number): Level | null =>
        getLevels(venueId).find((l) => l.id === levelId) ?? null;

    const getActiveIncidentsForLevel = (
        venueId: number,
        levelId: number | null,
    ): Incident[] => {
        const incidents = getActiveIncidents(venueId);
        return incidents
            .filter((i) => ["confirmed", "unconfirmed"].includes(i.status))
            .filter((i) => !levelId || i.levelId === levelId)
            .sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0));
    };

    /** O(1) lookup: levelId → venueId via index map */
    const getVenueIdForLevel = (levelId: number): number | undefined =>
        _levelVenueIndex.get(levelId);

    // ================================================================
    // ACTIONS - Fast Initialization (Current Venue First)
    // ================================================================

    /**
     * PHASE 1: Load venues list and current venue data immediately.
     * This is the critical path - must be fast for initial render.
     */
    async function initializeCurrentVenue(
        currentVenueId?: number,
    ): Promise<number | null> {
        if (initializing.value) return currentVenueId ?? null;

        initializing.value = true;
        initError.value = null;

        try {
            // Fetch all venues and levels list (fast, no incident data)
            const [venuesRes, levelsRes] = await Promise.all([
                edgeApi.get<Venue[]>("venues"),
                edgeApi.get<Level[]>("levels"),
            ]);

            allVenuesList.value = venuesRes.data ?? [];
            allLevelsList.value = levelsRes.data ?? [];
            _rebuildLevelVenueIndex();

            // Determine which venue to load first
            let targetVenueId = currentVenueId;
            if (!targetVenueId && allVenuesList.value.length > 0) {
                const defaultVenue = allVenuesList.value.find(
                    (v) => v.defaultVenue,
                );
                targetVenueId = defaultVenue?.id ?? allVenuesList.value[0].id;
            }

            if (!targetVenueId) {
                initialized.value = true;
                return null;
            }

            // Initialize target venue structure
            const venueData = ensureVenue(targetVenueId);
            venueData.venue =
                allVenuesList.value.find((v) => v.id === targetVenueId) ?? null;
            venueData.levels = allLevelsList.value.filter(
                (l) => l.venueId === targetVenueId,
            );

            // Fetch active incidents for current venue only (critical for header)
            await fetchActiveIncidents(targetVenueId);

            initialized.value = true;
            console.log(
                `[VenueDataStore] Current venue ${targetVenueId} ready (${venueData.activeIncidents.length} incidents)`,
            );

            return targetVenueId;
        } catch (e) {
            initError.value =
                e instanceof Error ? e.message : "Failed to initialize";
            console.error(`[VenueDataStore] initializeCurrentVenue error:`, e);
            return null;
        } finally {
            initializing.value = false;
        }
    }

    /**
     * PHASE 2: Load remaining venues in background (non-blocking).
     * Call this AFTER current venue is loaded and MQTT is connected.
     */
    async function initializeRemainingVenues(
        currentVenueId: number,
    ): Promise<void> {
        if (backgroundLoadComplete.value) return;

        const otherVenueIds = allVenuesList.value
            .filter((v) => v.id !== currentVenueId)
            .map((v) => v.id);

        if (otherVenueIds.length === 0) {
            backgroundLoadComplete.value = true;
            return;
        }

        console.log(
            `[VenueDataStore] Loading ${otherVenueIds.length} remaining venues in background...`,
        );

        // Initialize venue structures immediately (from cached lists)
        for (const venueId of otherVenueIds) {
            const venueData = ensureVenue(venueId);
            venueData.venue =
                allVenuesList.value.find((v) => v.id === venueId) ?? null;
            venueData.levels = allLevelsList.value.filter(
                (l) => l.venueId === venueId,
            );
        }

        // Fetch incidents for all other venues in parallel (background)
        const incidentPromises = otherVenueIds.map((venueId) =>
            fetchActiveIncidents(venueId).catch((e) => {
                console.warn(
                    `[VenueDataStore] Failed to fetch incidents for venue ${venueId}:`,
                    e,
                );
            }),
        );

        await Promise.all(incidentPromises);

        backgroundLoadComplete.value = true;
        console.log(
            `[VenueDataStore] All ${allVenuesList.value.length} venues initialized`,
        );
    }

    /**
     * Legacy method - initializes all venues (still works, but slower)
     * @deprecated Use initializeCurrentVenue + initializeRemainingVenues
     */
    async function initializeAllVenues(): Promise<void> {
        const venueId = await initializeCurrentVenue();
        if (venueId) {
            await initializeRemainingVenues(venueId);
        }
    }

    // ================================================================
    // ACTIONS - On-Demand Fetching
    // ================================================================

    /**
     * Fetch venue basics if not already loaded
     */
    async function fetchVenueBasics(
        venueId: number,
        force = false,
    ): Promise<VenueData> {
        const venueData = ensureVenue(venueId);

        if (venueData.venue && !force) return venueData;

        venueData.loading.venue = true;
        venueData.loading.levels = true;

        try {
            // Use cached lists if available
            if (allVenuesList.value.length > 0) {
                venueData.venue =
                    allVenuesList.value.find((v) => v.id === venueId) ?? null;
                venueData.levels = allLevelsList.value.filter(
                    (l) => l.venueId === venueId,
                );
            } else {
                const [venuesRes, levelsRes] = await Promise.all([
                    edgeApi.get<Venue[]>("venues"),
                    edgeApi.get<Level[]>("levels", { params: { venueId } }),
                ]);
                venueData.venue =
                    venuesRes.data?.find((v) => v.id === venueId) ?? null;
                venueData.levels = levelsRes.data ?? [];
            }
            venueData.lastFetched = new Date();
        } catch (e) {
            venueData.errors.venue =
                e instanceof Error ? e.message : "Failed to fetch venue";
            console.error(`[VenueDataStore] fetchVenueBasics error:`, e);
        } finally {
            venueData.loading.venue = false;
            venueData.loading.levels = false;
        }

        return venueData;
    }

    /**
     * Fetch active incidents for a venue
     */
    const _incidentsFetchInFlight = new Map<number, Promise<void>>();
    function fetchActiveIncidents(
        venueId: number,
        force = false,
    ): Promise<void> {
        return dedupeInFlight(_incidentsFetchInFlight, venueId, force, () =>
            _doFetchActiveIncidents(venueId),
        );
    }

    async function _doFetchActiveIncidents(venueId: number): Promise<void> {
        const venueData = ensureVenue(venueId);
        venueData.loading.incidents = true;
        venueData.errors.incidents = null;

        try {
            venueData.activeIncidents = await fetchAllPages<Incident>(
                "incidents",
                { venueId, status: ["confirmed", "unconfirmed"] },
            );

            // Rebuild _incidentVenueIndex entries for this venue: drop old
            // entries that mapped to it, then add current set. Keeps the
            // index authoritative for incident-scoped delta routing.
            const stale: number[] = [];
            _incidentVenueIndex.forEach((vid, iid) => {
                if (vid === venueId) stale.push(iid);
            });
            for (const iid of stale) _incidentVenueIndex.delete(iid);
            for (const inc of venueData.activeIncidents) {
                _incidentVenueIndex.set(inc.id, venueId);
            }
        } catch (e) {
            venueData.errors.incidents =
                e instanceof Error ? e.message : "Failed to fetch incidents";
            console.error(`[VenueDataStore] fetchActiveIncidents error:`, e);
        } finally {
            venueData.loading.incidents = false;
        }
    }

    const _usersFetchInFlight = new Map<number, Promise<EdgeUser[]>>();

    /** Fetch all users for a venue (stored at venue level, not per-level) */
    async function fetchVenueUsers(
        venueId: number,
        force = false,
    ): Promise<EdgeUser[]> {
        const venueData = ensureVenue(venueId);

        if (!force && _usersBootstrapped.has(venueId)) {
            return venueData.endUsers;
        }

        return dedupeInFlight(_usersFetchInFlight, venueId, force, () =>
            _doFetchVenueUsers(venueId),
        );
    }

    async function _doFetchVenueUsers(venueId: number): Promise<EdgeUser[]> {
        const venueData = ensureVenue(venueId);

        _usersBootstrapped.delete(venueId);
        venueData.loading.users = true;
        venueData.errors.users = null;

        try {
            const allUsers = await fetchAllPages<EdgeUser>("users", {
                venueId,
            });

            // Deduplicate (pagination with changing data can shift users between pages)
            const uniqueUsers = new Map<number, EdgeUser>();
            for (const u of allUsers) uniqueUsers.set(u.id, u);

            // markRaw prevents deep proxying of the entire array and its objects
            venueData.endUsers = markRaw(
                Array.from(uniqueUsers.values()).map((u) =>
                    markRaw(u as EdgeUser),
                ),
            );
            _rebuildUserVenueIndexForVenue(venueId, venueData.endUsers);
            venueData.usersLastFetched = new Date();
            _usersBootstrapped.add(venueId);
            console.log(
                `[VenueDataStore] Fetched ${uniqueUsers.size} users for venue ${venueId}`,
            );
            return venueData.endUsers;
        } catch (e) {
            venueData.errors.users =
                e instanceof Error ? e.message : "Failed to fetch users";
            console.error(`[VenueDataStore] fetchVenueUsers error:`, e);
            return [];
        } finally {
            venueData.loading.users = false;
        }
    }

    /**
     * Fetch the master safezone list for a venue from /safezones (venue-wide).
     * Safezones are small and slow-moving, so a single venue-scoped fetch is
     * the right grain. Level filtering happens client-side at the consumer.
     */
    const _safezoneFetchInFlight = new Map<number, Promise<Safezone[]>>();
    async function fetchVenueSafezones(
        venueId: number,
        force = false,
    ): Promise<Safezone[]> {
        const venueData = ensureVenue(venueId);

        if (!force && venueData.safezonesLastFetched) {
            return venueData.safezones;
        }

        const run = async () => {
            venueData.loading.safezones = true;
            venueData.errors.safezones = null;
            try {
                const safezones = await fetchAllPages<Safezone>("safezones", {
                    venueId,
                });
                venueData.safezones = safezones;
                venueData.safezonesLastFetched = new Date();
                // Bump version so viewContext computed re-evaluates.
                levelDataVersion.value++;
                return safezones;
            } catch (e) {
                venueData.errors.safezones =
                    e instanceof Error ? e.message : "Failed to fetch safezones";
                console.error(`[VenueDataStore] fetchVenueSafezones error:`, e);
                return venueData.safezones;
            } finally {
                venueData.loading.safezones = false;
            }
        };

        return dedupeInFlight(_safezoneFetchInFlight, venueId, force, run);
    }

    const _venueSpacesFetchInFlight = new Map<number, Promise<Space[]>>();

    /** Fetch all spaces for a venue venue-wide (for list-view filtering/display). */
    async function fetchVenueSpaces(
        venueId: number,
        force = false,
    ): Promise<Space[]> {
        const venueData = ensureVenue(venueId);

        if (!force && venueData.venueSpacesLastFetched) {
            return venueData.venueSpaces;
        }

        const run = async () => {
            venueData.loading.spaces = true;
            venueData.errors.spaces = null;
            try {
                const spaces = await fetchAllPages<Space>("spaces", { venueId });
                venueData.venueSpaces = spaces;
                venueData.venueSpacesLastFetched = new Date();
                return spaces;
            } catch (e) {
                venueData.errors.spaces =
                    e instanceof Error ? e.message : "Failed to fetch spaces";
                console.error(`[VenueDataStore] fetchVenueSpaces error:`, e);
                return venueData.venueSpaces;
            } finally {
                venueData.loading.spaces = false;
            }
        };

        return dedupeInFlight(_venueSpacesFetchInFlight, venueId, force, run);
    }

    /** Fetch comments for a specific incident */
    async function fetchIncidentComments(
        venueId: number,
        incidentId: number,
    ): Promise<IncidentComment[]> {
        const venueData = ensureVenue(venueId);

        try {
            const comments = await fetchAllPages<IncidentComment>(
                "incident-comments",
                { incidentId },
            );

            // Store in the incidentComments object
            venueData.incidentComments[incidentId] = comments;

            console.log(
                `[VenueDataStore] Fetched ${comments.length} comments for incident ${incidentId}`,
            );

            return comments;
        } catch (e) {
            console.error(`[VenueDataStore] fetchIncidentComments error:`, e);
            return [];
        }
    }

    // In-flight dedup: keyed by "venueId-levelId", holds the active promise.
    // Prevents the two simultaneous component mounts (MappedInMapComponent + watcher)
    // from each firing a full set of 5 parallel XHR requests for the same level.
    const _levelFetchInFlight = new Map<string, Promise<LevelData>>();

    /** Fetch level-specific data (devices, markers, safezones, spaces, buildings) */
    async function fetchLevelData(
        venueId: number,
        levelId: number,
        force = false,
    ): Promise<LevelData> {
        ensureVenue(venueId);
        const levelData = ensureLevel(venueId, levelId);

        if (!force && levelData.lastFetched) return levelData;

        // force=true (gap recovery) still gets a fresh request, queued after any in-flight one
        return dedupeInFlight(
            _levelFetchInFlight,
            `${venueId}-${levelId}`,
            force,
            () => _doFetchLevelData(venueId, levelId, levelData),
        );
    }

    async function _doFetchLevelData(
        venueId: number,
        levelId: number,
        levelData: LevelData,
    ): Promise<LevelData> {
        // Set loading state
        Object.keys(levelData.loading).forEach(
            (k) => ((levelData.loading as any)[k] = true),
        );

        try {
            // Fetch level-specific data (users + safezones are venue-scoped)
            const [devices, markers, spaces, buildings] = await Promise.all([
                fetchAllPages<EdgeDevice>("devices", { venueId, levelId }),
                fetchAllPages<Marker>("markers", {
                    venueId,
                    levelId,
                    status: ["confirmed", "unconfirmed"],
                }),
                fetchAllPages<Space>("spaces", { venueId, levelId }),
                fetchAllPages<Building>("buildings", { venueId, levelId }),
            ]);

            levelData.devices = devices;
            levelData.devices.forEach((d) =>
                _deviceLevelIndex.set(d.id, { venueId, levelId }),
            );

            levelData.markers = markers;
            levelData.markers.forEach((m) =>
                _markerLevelIndex.set(m.id, { venueId, levelId }),
            );

            levelData.spaces = spaces;
            levelData.buildings = buildings;
            levelData.lastFetched = new Date();
            // Increment version counter so viewContext computed (spaces/buildings) re-evaluates
            levelDataVersion.value++;
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Failed to fetch";
            Object.keys(levelData.errors).forEach(
                (k) => ((levelData.errors as any)[k] = msg),
            );
            console.error(`[VenueDataStore] fetchLevelData error:`, e);
        } finally {
            Object.keys(levelData.loading).forEach(
                (k) => ((levelData.loading as any)[k] = false),
            );
        }

        return levelData;
    }

    // ================================================================
    // ACTIONS - Delta Updates (MQTT)
    // ================================================================

    /** PATCH: Merge partial data with existing entity */
    function mergeById<T extends { id: number | string }>(
        arr: T[],
        item: Partial<T> & { id: number | string },
    ): T[] {
        const idx = arr.findIndex((x) => x.id === item.id);
        if (idx > -1) {
            // Mutate in-place to avoid creating a new reactive proxy object on every
            // MQTT PATCH update. Object.assign preserves the existing array slot's
            // reactive proxy, preventing createReactiveObject churn and GC pressure.
            Object.assign(arr[idx] as object, item);
        } else {
            console.warn(
                `[VenueDataStore] PATCH: entity ${item.id} not found — inserting as new`,
            );
            arr.push(item as T);
        }
        return arr;
    }

    /** PUT: Replace entire entity */
    function replaceById<T extends { id: number | string }>(
        arr: T[],
        item: Partial<T> & { id: number | string },
    ): T[] {
        const idx = arr.findIndex((x) => x.id === item.id);
        if (idx > -1) {
            arr[idx] = item as T;
        } else {
            arr.push(item as T);
        }
        return arr;
    }

    /** POST: Add new entity (or replace if exists) — alias for replaceById */
    function addById<T extends { id: number | string }>(
        arr: T[],
        item: Partial<T> & { id: number | string },
    ): T[] {
        return replaceById(arr, item);
    }

    /** DELETE: Remove entity by ID */
    function removeById<T extends { id: number | string }>(
        arr: T[],
        id: number | string,
    ): T[] {
        const idx = arr.findIndex((x) => x.id === id);
        if (idx > -1) {
            arr.splice(idx, 1);
        } else {
            console.warn(
                `[VenueDataStore] DELETE: entity ${id} not found — nothing to remove`,
            );
        }
        return arr;
    }

    /** Apply entity update based on HTTP method */
    function applyByMethod<T extends { id: number | string }>(
        arr: T[],
        item: Partial<T> & { id: number | string },
        method?: string,
    ): T[] {
        // Normalize method (support both HTTP style and create/update/delete style)
        const normalizedMethod = method?.toUpperCase();

        switch (normalizedMethod) {
            case "PATCH":
            case "UPDATE":
                return mergeById(arr, item);
            case "PUT":
                return replaceById(arr, item);
            case "POST":
            case "CREATE":
                return addById(arr, item);
            case "DELETE":
                return removeById(arr, item.id);
            default:
                // Default to merge for backward compatibility
                return mergeById(arr, item);
        }
    }

    /** Track missing users being fetched to avoid duplicate HTTP requests */
    const _fetchingMissingUsers = new Set<number>();

    /** Pending user IDs waiting to be batch-fetched (collected over a 250ms window) */
    const _pendingMissingUserIds = new Set<number>();
    let _missingUsersFlushTimer: ReturnType<typeof setTimeout> | null = null;

    /** Queue a missing user for batch fetching (only fires for bootstrapped venues) */
    function _deferFetchUser(userId: number): void {
        if (_fetchingMissingUsers.has(userId)) return;
        _pendingMissingUserIds.add(userId);
        if (!_missingUsersFlushTimer) {
            _missingUsersFlushTimer = setTimeout(_flushMissingUsers, 250);
        }
    }

    /** Flush all pending missing-user fetches as a batch */
    async function _flushMissingUsers(): Promise<void> {
        _missingUsersFlushTimer = null;
        const ids = [..._pendingMissingUserIds];
        _pendingMissingUserIds.clear();
        if (ids.length === 0) return;

        if (ids.length > 30) {
            const venueId = _userVenueIndex.get(ids[0]);
            if (venueId) {
                console.warn(
                    `[VenueDataStore] ${ids.length} missing users — re-fetching venue ${venueId}`,
                );
                await fetchVenueUsers(venueId, true);
                // The bulk path bypasses applyDelta's dirty-marking - mark
                // everything it touched so the hydrated profiles (names,
                // wearable flags) actually re-render.
                for (const uid of ids) _dirtyUserIds.add(uid);
                locationUpdateTick.value++;
            }
            return;
        }

        console.log(`[VenueDataStore] Fetching ${ids.length} missing user(s)`);
        await Promise.allSettled(ids.map((id) => fetchUserById(id)));
    }

    /** Fetch a single user by ID (called by batch mechanism for genuinely new users) */
    async function fetchUserById(userId: number): Promise<void> {
        if (_fetchingMissingUsers.has(userId)) return;
        _fetchingMissingUsers.add(userId);

        try {
            const res = await edgeApi.get<EdgeUser>(`users/${userId}`);
            const user = res.data;
            if (user) {
                let targetVenueId = user.venueId;

                if (!targetVenueId && user.levelId) {
                    const level = allLevelsList.value.find(
                        (l) => l.id === user.levelId,
                    );
                    if (level) targetVenueId = level.venueId;
                }

                if (targetVenueId) {
                    // Onsite path: let applyDelta own the mutation. It snapshots
                    // the pre-update levelId on the cached object to detect
                    // cross-level moves; hydrating here first would overwrite
                    // that snapshot and strand the old-level marker. The
                    // user-loop in applyDelta routes by user.venueId, which
                    // we set on the user object first.
                    user.venueId = targetVenueId;
                    applyDelta({
                        scope: "user",
                        method: "PUT",
                        users: [markRaw(user)],
                    });
                } else {
                    // Offsite path: applyDelta won't run, so hydrate the cached
                    // object directly. Critical to break the re-fetch loop —
                    // without this, offsite users (venueId=null from the API)
                    // would never get names written to the cache and the
                    // hasName check would re-queue them on every MQTT update.
                    const cached = _userObjectCache.get(userId);
                    if (cached) {
                        Object.assign(cached, user);
                        // This merge happens outside applyDelta, so signal it
                        // ourselves - otherwise nothing re-renders and the
                        // widget keeps showing the pre-fetch stub (e.g. a
                        // phone icon for a wearable user) until some later
                        // delta happens to touch this user.
                        _dirtyUserIds.add(userId);
                        locationUpdateTick.value++;
                    }
                }
            }
        } catch (e) {
            console.warn(`[VenueDataStore] Failed to fetch user ${userId}:`, e);
        } finally {
            _fetchingMissingUsers.delete(userId);
        }
    }

    /** Check if user is off-site (delegates to staticData) */
    function isUserOffsite(user: Partial<EdgeUser>): boolean {
        return staticDataStore.isOffsite(user.exposureLevel);
    }

    /**
     * Apply MQTT delta updates (PATCH=merge, PUT=replace, POST=add, DELETE=remove).
     *
     * Routing is per-entity: each block resolves its own venueId from the
     * entity's own fields. The store does not consult view-layer state —
     * there is no "currently viewed venue" fallback. Multi-venue and
     * multi-level batches are first-class.
     *
     * Resolution priority by entity:
     *   - incidents: inc.venueId → _incidentVenueIndex.get(inc.id)
     *   - comments: _incidentVenueIndex.get(c.incidentId)
     *   - markers: _levelVenueIndex.get(m.levelId)        (Marker has no venueId)
     *   - safezones: sz.venueId → _levelVenueIndex.get(sz.levelId)
     *   - devices: d.venueId → _levelVenueIndex.get(d.levelId)
     *   - users: per-user via fullUser.venueId
     * Entities that can't be routed are aggregated into a single console.warn
     * at the end of the call — never silently placed into the wrong venue.
     */
    function applyDelta(delta: VenueDelta): void {
        const {
            scope,
            method,
            incidents,
            markers,
            safeZones,
            users,
            devices,
            comments,
            incidentSafeZones,
            endUsersAssisting,
            incidentDuplicates,
        } = delta;

        // Parse delta.timestamp once for per-venue lastFetched stamping at
        // the end. Avoids re-parsing per entity.
        let validTimestamp: Date | null = null;
        if (delta.timestamp) {
            const date = new Date(delta.timestamp);
            if (!isNaN(date.getTime())) validTimestamp = date;
        }

        // Track every venue this delta mutates. Stamped exactly once each
        // at the end so a 50-entity same-venue burst yields one assign,
        // not 50 (avoiding wasted Vue reactivity setter calls). One empty
        // Set per applyDelta call is negligible against per-entity work.
        const touchedVenues = new Set<number>();
        const touch = (vid: number | null | undefined): void => {
            if (validTimestamp && typeof vid === "number") {
                touchedVenues.add(vid);
            }
        };

        // Accumulate skipped-entity reasons across all blocks and emit a
        // single console.warn at the end. Per-entity warns flooded the
        // console under degraded MQTT conditions (200-marker burst →
        // 200 lines/s); aggregating gives one log per delta with up to
        // the first 5 ids plus the total count.
        const SKIP_SAMPLE_LIMIT = 5;
        const skipSamples: string[] = [];
        let skipCount = 0;
        const recordSkip = (reason: string): void => {
            skipCount++;
            if (skipSamples.length < SKIP_SAMPLE_LIMIT) {
                skipSamples.push(reason);
            }
        };

        // Helper: track a (venue, level) pair in a per-venue Set, used by
        // markers and devices to cap "replace array ref per affected
        // level" operations once per affected level, regardless of how
        // many entities mutated that level.
        const trackLevel = (
            map: Map<number, Set<number>>,
            vId: number,
            lId: number,
        ): void => {
            let s = map.get(vId);
            if (!s) {
                s = new Set();
                map.set(vId, s);
            }
            s.add(lId);
        };

        const resolveByLevel = (
            lid: number | null | undefined,
        ): number | null => {
            if (typeof lid !== "number") return null;
            const v = _levelVenueIndex.get(lid);
            return v != null ? v : null;
        };

        // Capture incident-scope context BEFORE the incidents block runs.
        // The block deletes resolved incidents from _incidentVenueIndex,
        // and downstream blocks (incident-scoped relations, resolved-
        // incident marker cleanup) still need the incident's venue. Read
        // the incident's own venueId first — that's the source of truth
        // for newly-arriving incidents — then fall back to the index for
        // updates/removes that omit venueId.
        const targetIncidentId =
            scope === "incident" && incidents?.length === 1
                ? incidents[0].id
                : null;
        // scope=incident only carries relations for ONE incident at a time.
        // A multi-incident payload would silently drop relations because
        // targetIncidentId would be null — flag it so backend regressions
        // are visible.
        if (
            scope === "incident" &&
            incidents !== undefined &&
            incidents.length > 1
        ) {
            console.warn(
                `[VenueDataStore] applyDelta: scope=incident with ${incidents.length} incidents — relations payload (incidentSafeZones/endUsersAssisting/incidentDuplicates/comments) will be dropped`,
            );
        }
        const incidentIsResolved =
            scope === "incident" && incidents?.[0]?.status
                ? incidents[0].status !== "confirmed" &&
                  incidents[0].status !== "unconfirmed"
                : false;
        const scopeIncidentVenueId: number | null =
            targetIncidentId != null
                ? (typeof incidents?.[0]?.venueId === "number"
                      ? (incidents[0].venueId as number)
                      : (_incidentVenueIndex.get(targetIncidentId) ?? null))
                : null;

        // === INCIDENTS (per-incident routing) ===
        if (incidents) {
            for (const inc of incidents) {
                const incVenueId =
                    (typeof inc.venueId === "number"
                        ? inc.venueId
                        : null) ?? _incidentVenueIndex.get(inc.id) ?? null;
                if (incVenueId == null) {
                    recordSkip(`incident ${inc.id}`);
                    continue;
                }
                const vd =
                    venues.value.get(incVenueId) ?? ensureVenue(incVenueId);
                touch(incVenueId);

                const title = inc.title ?? (inc as any).name ?? null;
                const isResolvable =
                    inc.isResolvable ??
                    (inc as any).isResolveable ??
                    undefined;

                const idx = vd.activeIncidents.findIndex(
                    (i) => i.id === inc.id,
                );
                const isActive =
                    inc.status === "confirmed" || inc.status === "unconfirmed";

                if (!isActive && inc.status) {
                    // Resolved/cancelled — remove from activeIncidents.
                    if (idx > -1) vd.activeIncidents.splice(idx, 1);
                    if (inc.id in vd.incidentComments) {
                        delete vd.incidentComments[inc.id];
                    }
                    _incidentVenueIndex.delete(inc.id);
                } else if (idx > -1) {
                    // Mutate the existing reactive proxy in-place rather
                    // than replacing it with a spread object — replacement
                    // triggers createReactiveObject() on every MQTT update
                    // and forces downstream computeds to see a new
                    // reference, causing extra GC pressure and watcher
                    // churn.
                    const existing = vd.activeIncidents[idx];
                    Object.assign(existing, inc);
                    if (title !== null) existing.title = title;
                    if (isResolvable !== undefined)
                        existing.isResolvable = isResolvable;
                    // _incidentVenueIndex already correct.
                } else if (isActive) {
                    vd.activeIncidents.push({
                        ...inc,
                        title,
                        isResolvable,
                    } as Incident);
                    _incidentVenueIndex.set(inc.id, incVenueId);
                }
            }
        }

        // === INCIDENT-SCOPED RELATIONS (scope=incident: replace entirely) ===
        // Skipped on resolution: the incidents block above splices the
        // resolved incident out of activeIncidents and deletes its
        // _incidentVenueIndex entry, so any relations payload arriving on
        // the same delta would target a now-missing incident. Documenting
        // the drop explicitly rather than letting `find()` silently miss.
        if (
            scope === "incident" &&
            targetIncidentId &&
            scopeIncidentVenueId != null &&
            !incidentIsResolved
        ) {
            const vd = venues.value.get(scopeIncidentVenueId);
            if (vd) {
                const incident = vd.activeIncidents.find(
                    (i) => i.id === targetIncidentId,
                );
                if (incident) {
                    if (endUsersAssisting !== undefined) {
                        incident.endUsersAssisting =
                            endUsersAssisting as EndUserAssisting[];
                    }
                    if (incidentSafeZones !== undefined) {
                        incident.incidentSafeZones =
                            incidentSafeZones as IncidentSafeZone[];
                        // Each entry nests the current server-side safezone
                        // object — upsert into the venue master list so any
                        // field changes propagate to the map without
                        // waiting for a separate venue-scope safeZones
                        // delta. PATCH (merge) rather than PUT — the
                        // MQTT-nested safezone omits some fields the full
                        // /safezones fetch returns (e.g. safeZoneIncidentTypes).
                        let venueListChanged = false;
                        for (const isz of incidentSafeZones as IncidentSafeZone[]) {
                            const sz = isz.safezone;
                            if (!sz?.id) continue;
                            applyByMethod(vd.safezones, sz, "PATCH");
                            venueListChanged = true;
                        }
                        if (venueListChanged) {
                            vd.safezones = [...vd.safezones];
                            levelDataVersion.value++;
                        }
                    }
                    if (incidentDuplicates !== undefined) {
                        incident.incidentDuplicates =
                            incidentDuplicates as IncidentDuplicate[];
                    }
                    if (comments !== undefined) {
                        vd.incidentComments[targetIncidentId] =
                            comments as IncidentComment[];
                    }
                }
                touch(scopeIncidentVenueId);
            }
        } else if (comments) {
            // Non-incident scope: each comment routes via its incidentId
            // through _incidentVenueIndex.
            for (const c of comments) {
                const incId = c.incidentId ?? targetIncidentId;
                if (!incId) continue;
                const cVenueId = _incidentVenueIndex.get(incId) ?? null;
                if (cVenueId == null) {
                    recordSkip(`comment(incident=${incId})`);
                    continue;
                }
                const vd =
                    venues.value.get(cVenueId) ?? ensureVenue(cVenueId);
                const arr = vd.incidentComments[incId] ?? [];
                vd.incidentComments[incId] = applyByMethod(arr, c, method);
                touch(cVenueId);
            }
        }

        // === LEVEL-SPECIFIC DATA ===
        // NOTE: Do NOT set levelData.lastFetched here. That flag means
        // "full API fetch completed" and is used as a cache gate by
        // fetchLevelData(). MQTT deltas are incremental — setting
        // lastFetched from a delta poisons the cache, causing
        // fetchLevelData() to skip the actual API call and leave the
        // level with empty markers/devices/safezones.

        // === MARKERS (per-marker routing) ===
        if (markers) {
            if (
                scope === "incident" &&
                incidentIsResolved &&
                targetIncidentId &&
                scopeIncidentVenueId != null
            ) {
                // Incident resolved — remove all markers for it across
                // every level in its venue.
                const vd = venues.value.get(scopeIncidentVenueId);
                if (vd) {
                    const markerIds = markers.map((m) => m.id);
                    vd.levelData.forEach((lvl) => {
                        lvl.markers = lvl.markers.filter((m) => {
                            const remove =
                                markerIds.includes(m.id) ||
                                m.incidentId === targetIncidentId;
                            if (remove) _markerLevelIndex.delete(m.id);
                            return !remove;
                        });
                    });
                    touch(scopeIncidentVenueId);
                }
            } else {
                // Track (venue, level) pairs mutated so we can replace
                // array refs per affected level only — Vue 3.4+ early-
                // bailouts on same ref for unaffected levels.
                const mutatedByVenue = new Map<number, Set<number>>();
                for (const m of markers) {
                    const mLevelId = m.levelId;
                    if (mLevelId == null) continue;
                    // Marker schema has no venueId field — venue is
                    // derived from levelId via _levelVenueIndex.
                    const mVenueId = resolveByLevel(mLevelId);
                    if (mVenueId == null) {
                        recordSkip(`marker ${m.id}(level=${mLevelId})`);
                        continue;
                    }

                    const mLevelData = ensureLevel(mVenueId, mLevelId);

                    // Cross-level / cross-venue move: drop the marker from
                    // its previous location if different. Both venue and
                    // level live in the index, so the lookup is exact —
                    // no _levelVenueIndex fallback needed.
                    const prev = _markerLevelIndex.get(m.id);
                    if (
                        prev !== undefined &&
                        (prev.venueId !== mVenueId ||
                            prev.levelId !== mLevelId)
                    ) {
                        const oldVd = venues.value.get(prev.venueId);
                        const otherLevel = oldVd?.levelData.get(prev.levelId);
                        if (otherLevel) {
                            const idx = otherLevel.markers.findIndex(
                                (x) => x.id === m.id,
                            );
                            if (idx > -1) {
                                otherLevel.markers.splice(idx, 1);
                                trackLevel(
                                    mutatedByVenue,
                                    prev.venueId,
                                    prev.levelId,
                                );
                                touch(prev.venueId);
                            }
                        }
                    }
                    if (method?.toUpperCase() === "DELETE") {
                        _markerLevelIndex.delete(m.id);
                    } else {
                        _markerLevelIndex.set(m.id, {
                            venueId: mVenueId,
                            levelId: mLevelId,
                        });
                    }
                    applyByMethod(mLevelData.markers, m, method);
                    trackLevel(mutatedByVenue, mVenueId, mLevelId);
                    touch(mVenueId);
                }
                // Replace array refs per affected level.
                for (const [vid, levels] of mutatedByVenue) {
                    const vd = venues.value.get(vid);
                    if (!vd) continue;
                    for (const lid of levels) {
                        const ld = vd.levelData.get(lid);
                        if (ld) ld.markers = [...ld.markers];
                    }
                }
            }
        }

        // === SAFEZONES (per-safezone routing — venue-scoped master list) ===
        if (safeZones) {
            const mutatedSafezoneVenues = new Set<number>();
            for (const sz of safeZones) {
                const szVenueId =
                    (typeof sz.venueId === "number" ? sz.venueId : null) ??
                    resolveByLevel(sz.levelId ?? null);
                if (szVenueId == null) {
                    recordSkip(`safezone ${sz.id}`);
                    continue;
                }
                const vd =
                    venues.value.get(szVenueId) ?? ensureVenue(szVenueId);
                applyByMethod(vd.safezones, sz, method);
                mutatedSafezoneVenues.add(szVenueId);
                touch(szVenueId);
            }
            for (const vid of mutatedSafezoneVenues) {
                const vd = venues.value.get(vid);
                if (vd) vd.safezones = [...vd.safezones];
            }
        }

        // === DEVICES (per-device routing) ===
        if (devices) {
            const mutatedByVenue = new Map<number, Set<number>>();
            for (const d of devices) {
                const dLevelId = d.levelId;
                if (dLevelId == null) continue;
                const dVenueId =
                    (typeof d.venueId === "number" ? d.venueId : null) ??
                    resolveByLevel(dLevelId);
                if (dVenueId == null) {
                    recordSkip(`device ${d.id}(level=${dLevelId})`);
                    continue;
                }

                const dLevelData = ensureLevel(dVenueId, dLevelId);

                const prev = _deviceLevelIndex.get(d.id);
                if (
                    prev !== undefined &&
                    (prev.venueId !== dVenueId || prev.levelId !== dLevelId)
                ) {
                    const oldVd = venues.value.get(prev.venueId);
                    const otherLevel = oldVd?.levelData.get(prev.levelId);
                    if (otherLevel) {
                        const idx = otherLevel.devices.findIndex(
                            (x) => x.id === d.id,
                        );
                        if (idx > -1) {
                            otherLevel.devices.splice(idx, 1);
                            trackLevel(
                                mutatedByVenue,
                                prev.venueId,
                                prev.levelId,
                            );
                            touch(prev.venueId);
                        }
                    }
                }
                if (method?.toUpperCase() === "DELETE") {
                    _deviceLevelIndex.delete(d.id);
                } else {
                    _deviceLevelIndex.set(d.id, {
                        venueId: dVenueId,
                        levelId: dLevelId,
                    });
                }
                applyByMethod(dLevelData.devices, d, method);
                trackLevel(mutatedByVenue, dVenueId, dLevelId);
                touch(dVenueId);
            }
            for (const [vid, levels] of mutatedByVenue) {
                const vd = venues.value.get(vid);
                if (!vd) continue;
                for (const lid of levels) {
                    const ld = vd.levelData.get(lid);
                    if (ld) ld.devices = [...ld.devices];
                }
            }
        }

        // Signal to viewContext computed properties that level-scoped
        // data changed. The array-ref replacements above ensure Vue's
        // early-bailout (Object.is) only propagates to watchers for
        // levels that actually changed, not all levels on every MQTT tick.
        if (markers || safeZones || devices) {
            levelDataVersion.value++;
        }

        // === USERS (per-user routing — already correct, just integrate
        // touchedVenues into the unified set so user-only deltas stamp
        // their venues' lastFetched alongside other entity blocks) ===
        if (users) {
            for (const u of users) {
                const id = Number(u.id);
                const existing = _userObjectCache.get(id);

                // Snapshot pre-update levelId so we can detect cross-level
                // moves that stay within the same venue (not covered by
                // _moveUser, which only reacts to venue/offsite changes).
                const prevLevelId =
                    existing != null ? existing.levelId : undefined;

                let fullUser: EdgeUser;
                if (existing) {
                    Object.assign(existing, u);
                    fullUser = existing;
                } else {
                    fullUser = markRaw({ ...u } as EdgeUser);
                    _userObjectCache.set(id, fullUser);
                }

                // Level transition (including → null): queue stale-marker
                // cleanup. Cross-venue moves are already covered by
                // _moveUser → _removeUserFromLocation.
                if (
                    prevLevelId != null &&
                    fullUser.levelId !== prevLevelId
                ) {
                    _removedUserIds.add(id);
                }

                // Hydrate users whose full profile hasn't loaded. A name or
                // key alone (isEndUserHydrated - the render gate) is not
                // enough here: an MQTT location delta can carry the display
                // name but never carries `wearable`, and without it a
                // wearable user renders with the default phone icon forever.
                // An API-sourced profile always has the `wearable` key (null
                // for phone users), so a missing key means "profile not
                // fetched yet". Named stubs render immediately and their icon
                // corrects itself when the profile lands.
                if (!isEndUserHydrated(fullUser) || !("wearable" in fullUser)) {
                    _deferFetchUser(id);
                }

                _dirtyUserIds.add(id);
                for (const listener of _userUpdateListeners) listener(id);

                // Determine the real venue for this user. Users without a
                // venueId on their object are routed to offsiteUsers —
                // they must NOT inherit the delta's hint venueId, which
                // would pollute the user list with name-less ghosts.
                const userVenueId = fullUser.venueId ?? null;
                const targetVenueId = isUserOffsite(fullUser)
                    ? null
                    : userVenueId;

                const currentLocation: number | null | undefined =
                    _userVenueIndex.has(id)
                        ? _userVenueIndex.get(id)!
                        : _offsiteUserIds.has(id)
                          ? null
                          : undefined;

                if (
                    currentLocation === undefined ||
                    targetVenueId !== currentLocation
                ) {
                    _moveUser(fullUser, targetVenueId);
                }

                // Stamp lastFetched on both source and destination venues
                // (when each is a real venue, not offsite).
                touch(targetVenueId);
                if (
                    typeof currentLocation === "number" &&
                    currentLocation !== targetVenueId
                ) {
                    touch(currentLocation);
                }
            }

            if (users.length > 0) locationUpdateTick.value++;
        }

        // === STAMP lastFetched on all venues this delta touched ===
        if (validTimestamp && touchedVenues.size > 0) {
            for (const vid of touchedVenues) {
                const v = venues.value.get(vid);
                if (v) v.lastFetched = validTimestamp;
            }
        }

        // === REPORT skipped entities (one log per delta, sample + total) ===
        if (skipCount > 0) {
            const overflow =
                skipCount > skipSamples.length
                    ? ` (+${skipCount - skipSamples.length} more)`
                    : "";
            console.warn(
                `[VenueDataStore] applyDelta: skipped ${skipCount} unroutable entit${
                    skipCount === 1 ? "y" : "ies"
                }: ${skipSamples.join(", ")}${overflow}`,
            );
        }
    }

    // ================================================================
    // ACTIONS - Venue Switching
    // ================================================================

    /**
     * Prepare a venue for viewing — fetches basics and active incidents.
     * Does NOT include users; call fetchVenueUsers separately if needed.
     */
    async function prepareVenueForViewing(venueId: number): Promise<VenueData> {
        const venueData = ensureVenue(venueId);
        const promises: Promise<any>[] = [];

        // NOTE: Evaluate the incidents condition BEFORE calling fetchVenueBasics.
        // fetchVenueBasics uses the cached-list path (no awaits) when venue data
        // was already fetched, which means it runs synchronously and sets
        // venueData.lastFetched before the `!venueData.lastFetched` check below
        // could fire — causing fetchActiveIncidents to be silently skipped for any
        // venue that hasn't had its incidents fetched yet.
        // Also retry a failed fetch, or the venue stays without incidents
        const needsIncidentFetch =
            (venueData.activeIncidents.length === 0 &&
                !venueData.lastFetched) ||
            !!venueData.errors.incidents;

        if (!venueData.venue) {
            promises.push(fetchVenueBasics(venueId));
        }
        // An in-flight fetch (e.g. a superseded setup) must land before the venue is shown
        if (needsIncidentFetch || _incidentsFetchInFlight.has(venueId)) {
            promises.push(fetchActiveIncidents(venueId));
        }
        if (!venueData.safezonesLastFetched) {
            promises.push(fetchVenueSafezones(venueId));
        }

        if (promises.length > 0) {
            await Promise.all(promises);
        }

        return venueData;
    }

    // ================================================================
    // ACTIONS - Incident Operations
    // ================================================================

    /** Shared helper for incident action patches (resolve/confirm/etc.) */
    async function _patchIncidentAction(
        action: string,
        incidentId: number,
        requestData?: any,
    ): Promise<void> {
        await edgeApi.patch(
            `/incidents/${incidentId}/${action}`,
            requestData ? JSON.stringify(requestData) : {},
            { headers: { "Content-Type": "application/json" } },
        );
        // Store updated via MQTT - no manual update needed
    }

    async function resolveIncident(
        incidentId: number,
        requestData?: any,
    ): Promise<void> {
        return _patchIncidentAction("resolve", incidentId, requestData);
    }

    async function confirmIncident(
        incidentId: number,
        requestData?: any,
    ): Promise<void> {
        return _patchIncidentAction("confirm", incidentId, requestData);
    }

    /**
     * Promote an SOS incident to a real incident type.
     *
     * An SOS only ever exists as `unconfirmed` — it has no confirmed state of
     * its own, so it is resolved either by clearing it as a false alarm or by
     * promoting it here. Everything downstream (marker, status, timeline)
     * arrives over MQTT afterwards, exactly as it does for confirm.
     *
     * NOTE: promote lives on the IncidentsV2 controller (`/incidents-engine`),
     * not the `/incidents` root that _patchIncidentAction builds. The backend
     * confirmed mixing the two is fine — `/incidents` already delegates to v2
     * internally — so this deliberately does not go through that helper.
     */
    async function promoteIncident(
        incidentId: number,
        requestData: {
            type: string;
            venueId: number;
            levelId?: number;
            comment?: string;
        },
    ): Promise<void> {
        await edgeApi.patch(
            `/incidents-engine/${incidentId}/promote`,
            JSON.stringify(requestData),
            { headers: { "Content-Type": "application/json" } },
        );
        // Store updated via MQTT - no manual update needed
    }

    async function resolveDrillIncidents(): Promise<void> {
        const drillIncidents = allActiveIncidents.value.filter((i) => i.drill);

        // Reasons are per incident type - fetch each type once
        const incidentTypes = [...new Set(drillIncidents.map((i) => i.type))];
        const reasonsByType = new Map(
            await Promise.all(
                incidentTypes.map(
                    async (type) =>
                        [
                            type,
                            await fetchDrillResolveReason(type),
                        ] as const,
                ),
            ),
        );

        await Promise.all(
            drillIncidents.map((i) =>
                resolveIncident(i.id, {
                    reasonId: reasonsByType.get(i.type)!.id,
                }),
            ),
        );

        // Store updated via MQTT - no manual update needed
    }

    // ================================================================
    // ACTIONS - End User Operations
    // ================================================================

    async function markUserSafe(
        venueId: number,
        userId: number,
    ): Promise<void> {
        if (!venueId) {
            throw new Error("Venue ID is required for marking user safe");
        }
        if (!userId) {
            throw new Error("User ID is required for marking user safe");
        }

        await edgeApi.post(
            `/venues/${venueId}/mark-user-safe/${userId}`,
            {},
            {
                headers: { "Content-Type": "application/json" },
            },
        );
        // Store updated via MQTT - no manual update needed
    }

    async function clearUserSOS(userId: number): Promise<void> {
        await edgeApi.post(`/users/${userId}/actions/clear-sos`, {
            headers: { "Content-Type": "application/json" },
        });
        // Store updated via MQTT - no manual update needed
    }

    // ================================================================
    // ACTIONS - Cleanup
    // ================================================================

    /**
     * Trim transient user-related state on venue switch and re-sync the
     * lookup maps with the live arrays.
     *
     * The lookup maps (_userObjectCache, _userVenueIndex) are *rebuilt* —
     * not just cleared. Cache size equals the total user count across the
     * arrays anyway (entries are pointers to objects already held in
     * VenueData.endUsers / offsiteUsers), so clearing without rebuild gave
     * no real memory win and broke the invariant that every id sitting in
     * an array also has a cache + index entry. That broken invariant was
     * the source of the duplicate-end-user bug fixed on 2026-04-28: the
     * next MQTT delta took the cache-miss branch and _placeUser pushed a
     * second entry for an id that was already in the array.
     *
     * Doing the rebuild here (O(total users) once per venue switch) is far
     * cheaper than scanning arrays on every cache-miss inside the MQTT hot
     * path, and it makes the cache authoritative for all downstream
     * lookups, not only _removeUserFromLocation.
     *
     * _offsiteUserIds is intentionally not cleared (it already stays in
     * sync with offsiteUsers); _levelVenueIndex is venue-static.
     */
    function trimUserCaches(): void {
        _userObjectCache.clear();
        _userVenueIndex.clear();
        _dirtyUserIds.clear();
        _removedUserIds.clear();
        _fetchingMissingUsers.clear();
        _pendingMissingUserIds.clear();
        if (_missingUsersFlushTimer) {
            clearTimeout(_missingUsersFlushTimer);
            _missingUsersFlushTimer = null;
        }

        // Rebuild lookup maps from the live arrays so cache/index lookups
        // remain authoritative for every user already in the store.
        venues.value.forEach((vd, vid) => {
            for (const user of vd.endUsers) {
                _userObjectCache.set(user.id, user);
                _userVenueIndex.set(user.id, vid);
            }
        });
        for (const user of offsiteUsers.value) {
            _userObjectCache.set(user.id, user);
        }
    }

    function $reset(): void {
        venues.value.clear();
        allVenuesList.value = [];
        allLevelsList.value = [];
        offsiteUsers.value = [];
        _userVenueIndex.clear();
        _userObjectCache.clear();
        _offsiteUserIds.clear();
        _usersBootstrapped.clear();
        _usersFetchInFlight.clear();
        _incidentsFetchInFlight.clear();
        _levelVenueIndex.clear();
        _incidentVenueIndex.clear();
        _markerLevelIndex.clear();
        _deviceLevelIndex.clear();
        _fetchingMissingUsers.clear();
        _pendingMissingUserIds.clear();
        _dirtyUserIds.clear();
        _removedUserIds.clear();
        if (_missingUsersFlushTimer) {
            clearTimeout(_missingUsersFlushTimer);
            _missingUsersFlushTimer = null;
        }
        initialized.value = false;
        initializing.value = false;
        initError.value = null;
        backgroundLoadComplete.value = false;
    }

    // ================================================================
    // RETURN
    // ================================================================

    return {
        // State
        venues,
        allVenuesList,
        allLevelsList,
        offsiteUsers,
        initialized,
        initializing,
        initError,
        backgroundLoadComplete,

        // Aggregated getters
        allVenues,
        allActiveIncidents,
        totalIncidentCount,
        venueSummaries,
        criticalVenues,

        // Per-venue getters
        getVenueData,
        getVenue,
        getLevels,
        getActiveIncidents,
        getIncidentById,
        getIncidentComments,
        getLevelData,
        getDevices,
        getMarkers,
        getSafezones,
        getEndUsers,
        getAllEndUsersInVenue,
        getVenueEndUser,
        getOffsiteEndUser,
        getOffsiteUsers,
        getLevel,
        getActiveIncidentsForLevel,
        getVenueIdForLevel,

        // Initialization (prioritized)
        initializeCurrentVenue,
        initializeRemainingVenues,
        initializeAllVenues, // Legacy, still works

        // On-demand fetching
        fetchVenueBasics,
        fetchActiveIncidents,
        fetchVenueUsers,
        fetchVenueSafezones,
        fetchVenueSpaces,
        fetchIncidentComments,
        fetchLevelData,
        prepareVenueForViewing,

        // Delta updates
        applyDelta,
        locationUpdateTick,
        levelDataVersion,
        takeDirtyUserIds,
        takeRemovedUserIds,
        onUserUpdated,

        // Incident operations
        resolveIncident,
        confirmIncident,
        promoteIncident,
        resolveDrillIncidents,

        // End user operations
        markUserSafe,
        clearUserSOS,

        // Cleanup
        trimUserCaches,
        $reset,
    };
});

export type VenueDataStore = ReturnType<typeof useVenueDataStore>;
